import type { Geometry, Position } from 'geojson'

export type AppMapBounds = {
  east: number
  north: number
  south: number
  west: number
}

export type AppMapBoundsPolygon = {
  type: 'Polygon'
  coordinates: [
    [
      [number, number],
      [number, number],
      [number, number],
      [number, number],
      [number, number],
    ],
  ]
}

type MutableBounds = {
  east: number
  north: number
  south: number
  west: number
}

export type AppMapLngLatLike =
  | {
      lat: number
      lng: number
    }
  | {
      lat: () => number
      lng: () => number
    }

export type AppMapBoundsLike = {
  getNorthEast: () => AppMapLngLatLike
  getSouthWest: () => AppMapLngLatLike
}

function createEmptyBounds(): MutableBounds {
  return {
    east: -Infinity,
    north: -Infinity,
    south: Infinity,
    west: Infinity,
  }
}

function isValidBounds(bounds: MutableBounds) {
  return (
    Number.isFinite(bounds.east) &&
    Number.isFinite(bounds.north) &&
    Number.isFinite(bounds.south) &&
    Number.isFinite(bounds.west)
  )
}

function extendBoundsWithPosition(bounds: MutableBounds, position: Position) {
  const [lng, lat] = position

  if (typeof lng !== 'number' || typeof lat !== 'number') return

  bounds.east = Math.max(bounds.east, lng)
  bounds.north = Math.max(bounds.north, lat)
  bounds.south = Math.min(bounds.south, lat)
  bounds.west = Math.min(bounds.west, lng)
}

function extendBoundsWithGeometry(bounds: MutableBounds, geometry: Geometry) {
  if (geometry.type === 'GeometryCollection') {
    geometry.geometries.forEach((childGeometry) => {
      extendBoundsWithGeometry(bounds, childGeometry)
    })
    return
  }

  if (geometry.type === 'Point') {
    extendBoundsWithPosition(bounds, geometry.coordinates)
    return
  }

  flattenCoordinates(geometry.coordinates).forEach((position) => {
    extendBoundsWithPosition(bounds, position)
  })
}

function flattenCoordinates(coordinates: unknown): Position[] {
  if (!Array.isArray(coordinates)) return []

  if (
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  ) {
    return [coordinates as Position]
  }

  return coordinates.flatMap((coordinate) => flattenCoordinates(coordinate))
}

function hasLngLatMethods(
  lngLat: AppMapLngLatLike
): lngLat is { lat: () => number; lng: () => number } {
  return typeof lngLat.lng === 'function'
}

function readLngLat(lngLat: AppMapLngLatLike): [number, number] {
  if (hasLngLatMethods(lngLat)) {
    return [lngLat.lng(), lngLat.lat()]
  }

  return [lngLat.lng, lngLat.lat]
}

export function boundsLikeToAppMapBounds(
  bounds: AppMapBoundsLike | null | undefined
): AppMapBounds | null {
  if (!bounds) return null

  const [east, north] = readLngLat(bounds.getNorthEast())
  const [west, south] = readLngLat(bounds.getSouthWest())

  return {
    east,
    north,
    south,
    west,
  }
}

export function googleBoundsToAppMapBounds(
  bounds: google.maps.LatLngBounds | null | undefined
): AppMapBounds | null {
  return boundsLikeToAppMapBounds(bounds)
}

export function appMapBoundsToGeoWithinPolygon(
  bounds: AppMapBounds
): AppMapBoundsPolygon {
  return {
    type: 'Polygon',
    coordinates: [
      [
        [bounds.west, bounds.north],
        [bounds.east, bounds.north],
        [bounds.east, bounds.south],
        [bounds.west, bounds.south],
        [bounds.west, bounds.north],
      ],
    ],
  }
}

export function appMapBoundsContainsLngLat(
  bounds: AppMapBounds,
  coordinates: [number, number]
) {
  const [lng, lat] = coordinates
  const isWithinLat = lat >= bounds.south && lat <= bounds.north
  const isWithinLng =
    bounds.west <= bounds.east
      ? lng >= bounds.west && lng <= bounds.east
      : lng >= bounds.west || lng <= bounds.east

  return isWithinLat && isWithinLng
}

export function appMapBoundsToSuperclusterBbox(
  bounds: AppMapBounds
): [number, number, number, number] {
  return [bounds.west, bounds.south, bounds.east, bounds.north]
}

export function featuresToGoogleBoundsLiteral(
  features: Array<{ geometry: Geometry }>
): google.maps.LatLngBoundsLiteral | null {
  const bounds = createEmptyBounds()

  features.forEach((feature) => {
    extendBoundsWithGeometry(bounds, feature.geometry)
  })

  if (!isValidBounds(bounds)) return null

  return bounds
}
